import { Heart, MessageCircleMore } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useApp } from '../state/AppContext'
import type { Comment } from '../types'

interface Props {
  activityId: string
}

function formatWhen(iso: string) {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  return new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }).format(date)
}

function initial(name: string) {
  return name.trim().slice(0, 1).toUpperCase() || '?'
}

interface Thread {
  top: Comment
  replies: Comment[]
}

function buildThreads(comments: Comment[]): Thread[] {
  const byId = new Map(comments.map((item) => [item.id, item]))
  const childrenOf = (id: string) => comments.filter((item) => item.parentId === id)
  function descendants(id: string): Comment[] {
    const direct = childrenOf(id)
    return direct.flatMap((child) => [child, ...descendants(child.id)])
  }
  return comments
    .filter((item) => !item.parentId || !byId.has(item.parentId))
    .map((top) => ({ top, replies: descendants(top.id) }))
}

function CommentRow({ comment, onLike, onReply }: { comment: Comment; onLike: () => void; onReply: () => void }) {
  return (
    <div className={`comment ${comment.user.isBotelho ? 'comment--botelho' : ''} ${comment.user.isTeam ? 'comment--team' : ''}`}>
      <div className="comment__avatar">{initial(comment.user.name)}</div>
      <div className="comment__body">
        <p className="comment__meta">
          <b>{comment.user.name}</b>
          {comment.user.isBotelho && <span className="comment__badge">Leandro Botelho</span>}
          {comment.user.isTeam && <span className="comment__badge comment__badge--team">Equipe MúsicaComVocê</span>}
          <span className="comment__time">{formatWhen(comment.createdAt)}</span>
        </p>
        <p className="comment__text">{comment.text}</p>
        <div className="comment__actions">
          <button type="button" className={`comment__like ${comment.likedByMe ? 'is-liked' : ''}`} onClick={onLike}>
            <Heart fill={comment.likedByMe ? 'currentColor' : 'none'} />{comment.likeCount > 0 ? comment.likeCount : ''}
          </button>
          <button type="button" className="comment__reply-btn" onClick={onReply}>Responder</button>
        </div>
      </div>
    </div>
  )
}

export function Comments({ activityId }: Props) {
  const { comments, loadComments, postComment, toggleLike, busy } = useApp()
  const [text, setText] = useState('')
  const [replyingTo, setReplyingTo] = useState<Comment | null>(null)
  const [replyText, setReplyText] = useState('')

  useEffect(() => { void loadComments(activityId) }, [activityId]) // eslint-disable-line react-hooks/exhaustive-deps

  const threads = useMemo(() => buildThreads(comments), [comments])

  const submit = async () => {
    if (!text.trim()) return
    await postComment(activityId, text)
    setText('')
  }

  const submitReply = async (parentId: string) => {
    if (!replyText.trim()) return
    await postComment(activityId, replyText, parentId)
    setReplyText(''); setReplyingTo(null)
  }

  const replyBox = (target: Comment) => (
    <div className="comment__reply-box">
      <p className="comment__citing">respondendo a <b>{target.user.name}</b></p>
      <textarea value={replyText} onChange={(event) => setReplyText(event.target.value)} placeholder="Escreva sua resposta…" rows={2} />
      <div className="comment__reply-box__actions">
        <button type="button" className="text-button" onClick={() => { setReplyingTo(null); setReplyText('') }}>Cancelar</button>
        <button type="button" className="button button--primary" disabled={busy || !replyText.trim()} onClick={() => void submitReply(target.id)}>Enviar</button>
      </div>
    </div>
  )

  return (
    <section className="comments" aria-labelledby="comments-title">
      <MessageCircleMore />
      <p className="step-label">Quem também ouviu</p>
      <h2 id="comments-title">O que ficou para quem já passou por aqui.</h2>
      <div className="comments__composer">
        <textarea value={text} onChange={(event) => setText(event.target.value)} placeholder="O que esse encontro deixou em você?" rows={3} />
        <button type="button" className="button button--primary" disabled={busy || !text.trim()} onClick={() => void submit()}>Comentar</button>
      </div>
      {threads.length === 0 && <p className="comments__empty">Seja a primeira pessoa a comentar este encontro.</p>}
      <ul className="comments__list">
        {threads.map(({ top, replies }) => (
          <li key={top.id}>
            <CommentRow comment={top} onLike={() => void toggleLike(top.id)} onReply={() => setReplyingTo(top)} />
            {replyingTo?.id === top.id && replyBox(top)}
            {replies.length > 0 && (
              <ul className="comments__replies">
                {replies.map((reply) => {
                  const parent = comments.find((item) => item.id === reply.parentId)
                  return (
                    <li key={reply.id}>
                      {parent && parent.id !== top.id && <p className="comment__citing">respondendo a <b>{parent.user.name}</b></p>}
                      <CommentRow comment={reply} onLike={() => void toggleLike(reply.id)} onReply={() => setReplyingTo(reply)} />
                      {replyingTo?.id === reply.id && replyBox(reply)}
                    </li>
                  )
                })}
              </ul>
            )}
          </li>
        ))}
      </ul>
    </section>
  )
}
